import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@wbk/ui';
import { ContentfulRichText } from '@wbk/contentful';
import { type ContentfulRichText as ContentfulRichTextType } from '@wbk/contentful/api';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import { useOnClickOutside } from '@/hooks/use-on-click-outside';

interface TermsModalProps {
  description: ContentfulRichTextType;
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const ReadMoreDialog: React.FC<TermsModalProps> = ({ description, title, isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const { lang } = useParams<{ lang: Language }>();

  const { lock, unlock } = useScrollLock({ autoLock: false, widthReflow: true });

  const modalRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(modalRef as React.RefObject<HTMLElement>, () => {
    onClose();
  });

  useEffect(() => {
    if (isOpen) {
      lock();
    } else {
      unlock();
    }

    return () => unlock();
  }, [isOpen, lock, unlock]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm'>
      <div
        ref={modalRef}
        className='flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-white/10 bg-[#1E1E1E] shadow-2xl'
      >
        <div className='flex items-center justify-between border-b border-white/10 p-6'>
          <h3 className='text-xl font-bold text-white'>{title}</h3>
          <button
            onClick={onClose}
            className='text-gray-400 transition-colors hover:text-white'
            type='button'
          >
            <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        <div
          className='custom-scrollbar flex-grow space-y-8 overflow-y-auto p-6 text-sm leading-relaxed text-gray-300'
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
        >
          {description && <ContentfulRichText description={description} />}
        </div>

        <div className='flex flex-col justify-end gap-3 rounded-b-2xl border-t border-white/10 bg-white/5 p-6 sm:flex-row sm:items-center'>
          <div className='flex flex-col gap-3 sm:flex-row'>
            <Button
              theme='transparent'
              onClick={onClose}
              className='rounded-full bg-white px-6 py-2 text-center font-semibold text-black transition-colors hover:bg-gray-200'
            >
              {t('sponsor_form.buttons.close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadMoreDialog;
